/**
 * Fuzzy scope search for the practice picker.
 *
 * Engine, not semantic. Matches a typed query against a per-item corpus
 * built from:
 *   - primary  = the topic / concept canonical name (heaviest weight)
 *   - alias    = backward-injected abbreviations derived from the item's OWN
 *                name (e.g. "Cost-Volume-Profit" → "cvp"), plus the
 *                concept_id de-snake-cased. NEVER derived from chapter
 *                membership — chapter is not a product dimension.
 *   - descr    = sub_skill descriptions (rich natural-language text) — so a
 *                phrase like "target profit" lands on a concept whose sub-
 *                skills include that wording even if it isn't in the name.
 *
 * Tolerance:
 *   - case / punctuation / whitespace folded
 *   - hyphenated AND joined forms both match ("breakeven" ≡ "break-even" ≡
 *     "break even") via a joined-substring pass
 *   - light plural stemming (trailing 's' stripped) so "ethics" matches
 *     "ethical", "rates" matches "rate", etc.
 *   - explicit alias expansion both directions: query "cvp" → also tries
 *     "cost volume profit"; corpus alias field contains "cvp" for any item
 *     whose own name contains "cost-volume-profit".
 *
 * Scoring per (query-token, field) pair:
 *   exact-word       100
 *   exact-stem-word   95
 *   word-prefix       80
 *   stem-prefix       75
 *   joined-substring  60        (only when query ≥ 4 chars)
 *   general-substring 30        (only when query ≥ 3 chars)
 *
 * Per-field weight multipliers: primary 3.0 / alias 2.5 / descr 0.4.
 * If all query tokens matched something, multiply final by 1.4. Concepts win
 * ties (×1.02) since they're the more specific scope.
 *
 * The instrumentation layer logs raw queries → selections. The actual
 * semantic / embedding intent matcher gets built post-pilot with that data.
 */
import type { CoverageTree } from "./api";

export type ItemKind = "topic" | "concept";

export interface SearchItem {
  kind: ItemKind;
  label: string;
  slug?: string;                  // present when kind="topic"
  concept_id?: string;            // present when kind="concept"
  topic_slug?: string;            // concept's parent topic slug (for selection + display)
  topic_name?: string;            // concept's parent topic name (for breadcrumb in suggestion)
  n_questions: number;
  // search corpus (all pre-normalized)
  primary: string;
  alias: string;
  descr: string;
}

export interface MatchResult {
  item: SearchItem;
  score: number;
}

// Bidirectional corpus-side aliases. An item whose own name contains either
// form (abbreviation OR long phrase) gets BOTH forms injected into its alias
// field. The typed query is then matched as-is — no query-side expansion that
// would otherwise turn "cvp" into the three loose tokens "cost", "volume",
// "profit" and rake in unrelated cost-accounting concepts.
function aliasesForName(raw: string): string {
  const n = raw.toLowerCase();
  // word-boundary check for the abbreviation forms (so "cvp" in a name like
  // "Multi-Product CVP and Sales Mix" gets caught but a name accidentally
  // containing the letters c-v-p in some longer word does not).
  const padded = " " + n.replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim() + " ";
  const out: string[] = [];

  if (n.includes("cost-volume-profit") || n.includes("cost volume profit") || padded.includes(" cvp ")) {
    out.push("cvp", "cost volume profit", "cost-volume-profit");
  }
  if (n.includes("contribution margin") || padded.includes(" cm ")) {
    out.push("cm", "contribution margin");
  }
  if (n.includes("break-even") || n.includes("breakeven") || n.includes("break even")) {
    out.push("bep", "breakeven", "break-even", "break even");
  }
  if (n.includes("margin of safety") || padded.includes(" mos ")) {
    out.push("mos", "margin of safety");
  }
  if (n.includes("operating leverage") || padded.includes(" dol ")) {
    out.push("dol", "operating leverage");
  }
  if (n.includes("cost of goods manufactured") || padded.includes(" cogm ")) {
    out.push("cogm", "cost of goods manufactured");
  }
  if (n.includes("cost of goods sold") || padded.includes(" cogs ")) {
    out.push("cogs", "cost of goods sold");
  }
  if (n.includes("manufacturing") || padded.includes(" mfg ")) {
    out.push("mfg", "manufacturing");
  }
  if (n.includes("certified management accountant") || padded.includes(" cma ")) {
    out.push("cma", "certified management accountant");
  }
  if (n.includes("certified public accountant") || padded.includes(" cpa ")) {
    out.push("cpa", "certified public accountant");
  }
  if (n.includes("work in process") || n.includes("work-in-process") || padded.includes(" wip ")) {
    out.push("wip", "work in process");
  }
  if (n.includes("high-low") || n.includes("high low") || padded.includes(" high low ")) {
    out.push("high low", "high-low");
  }
  return out.join(" ");
}

export function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stem(token: string): string {
  if (token.length > 3 && token.endsWith("s") && !token.endsWith("ss")) {
    return token.slice(0, -1);
  }
  return token;
}

// Match the typed query as-is. No query-side alias expansion: abbreviations
// are handled by bidirectional injection on the CORPUS side (see
// `aliasesForName`). That keeps short abbreviations from blowing up into
// three loose tokens that happen to share words with the rest of the bank.
function expandQuery(raw: string): string[] {
  const norm = normalize(raw);
  return norm ? [norm] : [];
}

export function buildSearchIndex(tree: CoverageTree): SearchItem[] {
  const items: SearchItem[] = [];
  for (const t of tree.topics) {
    items.push({
      kind: "topic",
      label: t.name,
      slug: t.slug,
      n_questions: t.n_questions,
      primary: normalize(t.name),
      alias: normalize(aliasesForName(t.name)),
      descr: "",
    });
    for (const c of t.concepts) {
      const cAlias = [
        aliasesForName(c.name),
        c.concept_id.replace(/_/g, " "),
        // Parent topic name folded into the concept's alias so a query that
        // matches the topic name also surfaces the concepts beneath it
        // (e.g. "ethics" → ethical concepts under Ethics in Management
        // Accounting). This is taxonomy, not chapter.
        t.name,
      ]
        .filter(Boolean)
        .join(" ");
      const subDescr = c.sub_skills.map((s) => s.description).join(" ");
      items.push({
        kind: "concept",
        label: c.name,
        concept_id: c.concept_id,
        topic_slug: t.slug,
        topic_name: t.name,
        n_questions: c.n_questions,
        primary: normalize(c.name),
        alias: normalize(cAlias),
        descr: normalize(subDescr),
      });
    }
  }
  return items;
}

function scoreField(query: string, queryStem: string, field: string): number {
  if (!field || !query) return 0;
  const padded = " " + field + " ";
  const joined = field.replace(/ /g, "");
  const words = field.split(" ");

  if (padded.includes(" " + query + " ")) return 100;
  if (queryStem !== query && padded.includes(" " + queryStem + " ")) return 95;
  if (query.length >= 2 && words.some((w) => w.startsWith(query))) return 80;
  if (queryStem.length >= 2 && queryStem !== query && words.some((w) => w.startsWith(queryStem)))
    return 75;
  if (query.length >= 4 && joined.includes(query)) return 60;
  if (query.length >= 3 && field.includes(query)) return 30;
  return 0;
}

export function search(rawQuery: string, items: SearchItem[], limit = 8): MatchResult[] {
  const variants = expandQuery(rawQuery);
  if (variants.length === 0) return [];

  const results: MatchResult[] = [];
  for (const item of items) {
    let best = 0;
    for (const variant of variants) {
      const qWords = variant.split(" ").filter(Boolean);
      if (qWords.length === 0) continue;

      let total = 0;
      let allMatched = true;
      for (const qw of qWords) {
        const qs = stem(qw);
        const pri = scoreField(qw, qs, item.primary) * 3.0;
        const ali = scoreField(qw, qs, item.alias) * 2.5;
        const desc = scoreField(qw, qs, item.descr) * 0.4;
        const wordBest = Math.max(pri, ali, desc);
        if (wordBest === 0) allMatched = false;
        total += wordBest;
      }
      if (allMatched) total *= 1.4;
      if (item.kind === "concept") total *= 1.02;
      if (total > best) best = total;
    }
    if (best > 0) results.push({ item, score: best });
  }

  // Topic-promotion pass: when a topic is in the result set AND ≥2 of its
  // concepts are also in the result set, the topic represents the
  // "practice all of these" intent (a student typing "break-even" almost
  // always wants the whole area mixed, not just one breakeven concept).
  // Boost is anchored to the AVERAGE child score so weak/incidental child
  // matches can't inflate the topic above genuinely better-matched siblings.
  // For example: "product cost" gives Multi-Product CVP children weak ~357
  // scores via the "product" word; the period_vs_product_costs concept
  // scores 785. The topic boost would land at ~437 — below the 785 concept,
  // which is correct.
  const topicIndex = new Map<string, MatchResult>();
  for (const r of results) {
    if (r.item.kind === "topic" && r.item.slug) topicIndex.set(r.item.slug, r);
  }
  const childrenByTopic = new Map<string, MatchResult[]>();
  for (const r of results) {
    if (r.item.kind === "concept" && r.item.topic_slug && topicIndex.has(r.item.topic_slug)) {
      const list = childrenByTopic.get(r.item.topic_slug) ?? [];
      list.push(r);
      childrenByTopic.set(r.item.topic_slug, list);
    }
  }
  for (const [topicSlug, children] of childrenByTopic) {
    if (children.length < 2) continue;
    const topicResult = topicIndex.get(topicSlug);
    if (!topicResult) continue;
    const avgChild = children.reduce((s, r) => s + r.score, 0) / children.length;
    // Pull the topic to ~80 points above its average child match. If the
    // topic's own score is already higher (typed query strongly matched the
    // topic's name), leave it alone.
    topicResult.score = Math.max(topicResult.score, avgChild + 80);
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
}
